import { Component, ChangeDetectionStrategy} from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { DatePipe } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
  selector: 'app-custom-datepicker',
  templateUrl: './custom-datepicker.component.html',
  styleUrls: ['./custom-datepicker.component.scss']
})
export class CustomDatepickerComponent implements ICellRendererAngularComp {

  public params: any;
  public inputDate: any;
  public maxDate: Date;
  public minDate: Date;
  public startDate: Date;
  public isCellValue: boolean;
  public hideDatePicker: boolean = false;
  public disablePointerEvent: boolean = false;

  today: Date = new Date();
  commonMinDate: Date = new Date(2015, 0, 1);

  agInit(params: any) {
    this.params = params;
    if (params.column.colId === 'admitDate') {
      this.initAdmitDate(params);
    } else if (params.column.colId === 'dateOfRDDoc') {
      this.initDateOfRDDoc(params);
    } else if (params.column.colId === 'dischargedDate') {
      this.initDischargedDate(params);
    }

    this.initInputDate(params);
    this.isCellValue = !!this.inputDate;
    this.initLTCNutritionChange(params);
  }

  private hasErrorMarker(value: string): boolean {
    return value.indexOf('(Missing Required Data)') > -1 ||
      value.indexOf('(Data Error)') > -1 ||
      value.indexOf('(MRN_Duplicate Error)') > -1;
  }

  private parseDateString(dateStr: string): Date {
    return new Date(dateStr.split('-').reverse().join('/'));
  }

  private resolveDateBound(value: any, fallback: Date): Date {
    if (typeof value === 'string') {
      return this.hasErrorMarker(value) ? fallback : this.parseDateString(value);
    }
    return value;
  }

  private isValidDateString(value: any): boolean {
    return value && value.toString().indexOf('(Missing Required Data)') === -1 &&
      value.toString().indexOf('Data Error') === -1 &&
      value.toString().indexOf('(MRN_Duplicate Error)') === -1;
  }

  private initAdmitDate(params: any) {
    let tempMaxDate = params.data.dateOfRDDoc ? params.data.dateOfRDDoc : params.data.dischargedDate ? params.data.dischargedDate : this.today;
    this.maxDate = this.resolveDateBound(tempMaxDate, this.today);
    this.minDate = this.commonMinDate;
  }

  private initDateOfRDDoc(params: any) {
    let tempMinDate = params.data.admitDate ? params.data.admitDate : this.commonMinDate;
    let tempMaxDate = params.data.dischargedDate ? params.data.dischargedDate : this.today;
    if (this.isValidDateString(params.data.admitDate)) {
      this.startDate = this.parseDateString(params.data.admitDate);
    }
    this.minDate = this.resolveDateBound(tempMinDate, this.commonMinDate);
    this.maxDate = this.resolveDateBound(tempMaxDate, this.today);
  }

  private initDischargedDate(params: any) {
    if (this.isValidDateString(params.data.dateOfRDDoc)) {
      this.startDate = this.parseDateString(params.data.dateOfRDDoc);
    } else if (this.isValidDateString(params.data.admitDate)) {
      this.startDate = this.parseDateString(params.data.admitDate);
    }
    let tempMinDate = params.data.dateOfRDDoc ? params.data.dateOfRDDoc : params.data.admitDate ? params.data.admitDate : this.commonMinDate;
    this.minDate = this.resolveDateBound(tempMinDate, this.commonMinDate);
    this.maxDate = this.today;
  }

  private initInputDate(params: any) {
    if (params.value) {
      if (this.hasErrorMarker(params.value.toString())) {
        this.inputDate = params.value;
        this.hideDatePicker = true;
      } else {
        this.inputDate = this.parseDateString(params.value);
      }
    }
  }

  private initLTCNutritionChange(params: any) {
    if (params && params.sourcePage && params.sourcePage === 'LTCNutritionChange') {
      this.maxDate = this.today;
      this.minDate = this.commonMinDate;
      if (params.data.ltcNutritionStatusChangeId === 0 || params.data.ltcNutritionStatusChangeId === '0') {
        this.disablePointerEvent = false;
        if (params.minDate) {
          this.minDate = this.parseDateString(params.minDate);
        }
      } else {
        this.isCellValue = false;
        this.disablePointerEvent = true;
      }
    }
  }

  updateDate() {
    let datePipe = new DatePipe("en-US");
    if (this.inputDate) {
      this.params.data[this.params.column.colId] = datePipe.transform(this.inputDate, 'MM/dd/yyyy');
    }
    else {
      this.params.data[this.params.column.colId] = "";
    }

    if (this.params.column.colId === 'admitDate') {
      this.params.context.componentParent.customRefresh(['admitDate', 'dateOfRDDoc', 'dischargedDate']);
    } else if (this.params.column.colId === 'dateOfRDDoc') {
      this.params.context.componentParent.customRefresh(['admitDate', 'dateOfRDDoc', 'dischargedDate']);
    } else if (this.params.column.colId === 'dischargedDate') {
      if (this.params.data['dischargedDate']) {
        if (this.params.data['dischargedDate'].toString().trim() === '(Missing Required Data)' || this.params.data['dischargedDate'].toString().trim() === 'Data Error') {
          this.params.data.drgwMalnutrition = '';
          this.params.data.drgwoMalnutrition = '';
        }
      } else if (this.params.data['dischargedDate'] === '') {
        this.params.data.drgwMalnutrition = '';
        this.params.data.drgwoMalnutrition = '';
      }
      this.params.context.componentParent.customRefresh(['drgwMalnutrition', 'drgwoMalnutrition', 'dischargedDate', 'admitDate', 'dateOfRDDoc']);
    }
    else if (this.params.column.colId === 'statusChangeDate') {
      this.params.context.componentParent.customRefresh(['statusChangeDate']);
    }
    else if (this.params.column.colId === 'residentExpiredOrDischargedDate') {
      if (this.params.data['residentExpiredOrDischargedDate'] === '') {
        this.params.data.reasonforDischarge = '';
      }
      this.params.context.componentParent.customRefresh(['residentExpiredOrDischargedDate', 'reasonforDischarge']);
    }
  }

  datePickerClosed() {
    if (
      this.inputDate &&
      (this.inputDate.toString().indexOf('(Missing Required Data)') > -1 || this.inputDate.toString().indexOf('Data Error') > -1 ||
       this.inputDate.toString().indexOf('(MRN_Duplicate Error)') > -1)
    ) {
      this.hideDatePicker = true;
    }
  }

  clearText() {
    this.inputDate = '';
    this.params.data[this.params.column.colId] = '';
    this.params.context.componentParent.customRefresh(this.params.column.colId);
    this.isCellValue = false;
    if (this.params.column.colId === 'admitDate') {
      this.params.context.componentParent.customRefresh(['admitDate', 'dateOfRDDoc', 'dischargedDate']);
    } else if (this.params.column.colId === 'dateOfRDDoc') {
      this.params.context.componentParent.customRefresh(['dateOfRDDoc', 'dischargedDate']);
    } else if (this.params.data.dischargedDate) {
      this.params.data.drgwMalnutrition = '';
      this.params.data.drgwoMalnutrition = '';
      this.params.context.componentParent.customRefresh(['drgwMalnutrition', 'drgwoMalnutrition', 'dischargedDate']);
    }
    else if (this.params.column.colId === 'residentExpiredOrDischargedDate') {
      this.params.data.reasonforDischarge = '';
      this.params.context.componentParent.customRefresh(['residentExpiredOrDischargedDate', 'reasonforDischarge']);
    }
  }

  refresh(): boolean {
    return false;
  }
}
